import { Component, HostListener, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { JobRejectPopupComponent } from '../job-reject-popup/job-reject-popup.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { JobAcceptPopupComponent } from '../job-accept-popup/job-accept-popup.component';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { RecruitmentJobService } from '@edtech-ui/core/services/recruitment-management/recruitment-job.service';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, Subscription, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CompanyPendingJob } from '@edtech-ui/core/models/company-management/get-all-pending-company';
import { CompanyJobApprovePayload, CompanyJobRejectPayload } from '@edtech-ui/core/models/company-management/job-approval-approved-status';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
	selector: 'app-company-job-approval',
	standalone: true,
	imports: [CommonModule, FormsModule, MatDialogModule, MatProgressSpinnerModule],
	templateUrl: './company-job-approval.component.html',
	styleUrl: './company-job-approval.component.scss'
})
export class CompanyJobApprovalComponent implements OnInit, OnDestroy {

	companyId = ''
	searchTerm = '';
	selectedCompany = 'Companies';
	selectedType = 'All Types';
	selectedStatus = 'All Status';
	selectedJobRole = 'Job Roles';
	selectedWorkMode = 'Work Modes';
	companyDropdownOpen = false;
	typeDropdownOpen = false;
	statusDropdownOpen = false;
	jobRoleDropdownOpen = false;
	workModeDropdownOpen = false;
	isLoading = false;
	subscription = new Subscription();
	jobApprovals: CompanyPendingJob[] = [];
	allJobApprovals: CompanyPendingJob[] = [];
	total: number = 0;
	page: number = 1;
	limit: number = 10;
	totalPages: number = 0;
	searchSubject = new Subject<string>();
	statuses: string[] = ['All Status', 'Pending', 'Approved', 'Rejected'];
	types = ['All Types', 'Full-Time', 'Part-Time', 'Internship/Training', 'Contract'];
	companies: string[] = ['All Companies'];
	jobRoles: string[] = ['All Job Roles'];
	workModes: string[] = ['All Work Modes'];
	sortColumn = '';
	sortDirection: 'asc' | 'desc' = 'asc';
	showingFrom = 0;
	showingTo = 0;
	selectedSubmittedDate = '';
	selectedDeadlineDate = '';


	constructor(
		private router: Router,
		private dialog: MatDialog,
		private elementRef: ElementRef,
		private jobService: RecruitmentJobService,
		private toastr: ToastrService,
		private route: ActivatedRoute
	) { }


	ngOnInit(): void {
		const initialPage = Number(this.route.snapshot.queryParams['page']);
		if (initialPage && !isNaN(initialPage) && initialPage > 0) {
			this.page = initialPage;
		}
		this.loadFilterSuggestions();
		this.getAllPendingCompany();
		this.subscription.add(
			this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
				this.getAllPendingCompany();
			})
		);
		this.subscription.add(
			this.route.queryParams.subscribe(params => {
				const queryPage = Number(params['page']);
				if (queryPage && !isNaN(queryPage) && queryPage > 0 && queryPage !== this.page) {
					this.page = queryPage;
					this.getAllPendingCompany();
				}
			})
		);
	}

	loadFilterSuggestions(): void {
		this.subscription.add(
			this.jobService.getCompanyPendingApproval(1, 100).subscribe({
				next: (response) => {
					if (response.Success && response.Data?.jobs) {
						const jobs = response.Data.jobs;
						this.companies = ['All Companies', ...Array.from(new Set(jobs.map(job => job.company_name).filter(Boolean))).sort() as string[]];
						this.jobRoles = ['All Job Roles', ...Array.from(new Set(jobs.map(job => job.job_title).filter(Boolean))).sort() as string[]];
						this.workModes = ['All Work Modes', ...Array.from(new Set(jobs.map(job => job.work_mode).filter(Boolean))).sort() as string[]];
					}
				}
			})
		);
	}

	prevPage(): void {
		if (this.page > 1) {
			this.page--;
			this.getAllPendingCompany();
		}
	}

	nextPage(): void {
		if (this.page < this.totalPages) {
			this.page++;
			this.getAllPendingCompany();
		}
	}


	getAllPendingCompany() {
		const params: any = {};

		if (this.searchTerm?.trim()) {
			params.search = this.searchTerm.trim();
		}

		if (this.selectedType !== 'All Types') {
			params.job_type = this.selectedType;
		}

		if (this.selectedStatus !== 'All Status') {
			params.status = this.selectedStatus.toLowerCase();
		}

		const filter: any = {};
		if (this.selectedCompany !== 'Companies' && this.selectedCompany !== 'All Companies') {
			filter.company_name = this.selectedCompany;
		}
		if (this.selectedJobRole !== 'Job Roles' && this.selectedJobRole !== 'All Job Roles') {
			filter.job_title = this.selectedJobRole;
		}
		if (this.selectedWorkMode !== 'Work Modes' && this.selectedWorkMode !== 'All Work Modes') {
			filter.work_mode = this.selectedWorkMode;
		}
		if (this.selectedSubmittedDate) {
			filter.submitted_at = this.selectedSubmittedDate;
		}
		const filterPayload = Object.keys(filter).length > 0 ? filter : undefined;

		this.isLoading = true;

		this.subscription.add(
			this.jobService.getCompanyPendingApproval(
				this.page,
				this.limit,
				params.status,
				params.search,
				params.job_type,
				filterPayload
			).pipe(
				tap(response => {
					if (response.Success) {
						this.jobApprovals = response.Data.jobs || [];
						this.total = response.Data.total;
						this.page = response.Data.page;
						this.limit = response.Data.limit;
						this.totalPages = response.Data.total_pages;
						this.showingFrom = this.total === 0 ? 0 : ((this.page - 1) * this.limit) + 1;
						this.showingTo = Math.min(this.page * this.limit, this.total);
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong. Please try again');
					return of(null);
				})
			).subscribe()
		);
	}


	onDateChange(): void {
		this.page = 1;
		this.getAllPendingCompany();
	}

	clearSubmittedDateFilter(): void {
		this.selectedSubmittedDate = '';
		this.page = 1;
		this.getAllPendingCompany();
	}

	clearDeadlineDateFilter(): void {
		this.selectedDeadlineDate = '';
		this.page = 1;
		this.getAllPendingCompany();
	}

	resetFilters(): void {
		this.searchTerm = '';
		this.selectedCompany = 'Companies';
		this.selectedType = 'All Types';
		this.selectedStatus = 'All Status';
		this.selectedJobRole = 'Job Roles';
		this.selectedWorkMode = 'Work Modes';
		this.selectedSubmittedDate = '';
		this.selectedDeadlineDate = '';
		this.page = 1;
		this.getAllPendingCompany();
	}


	postCompnayApproveJob(jobId: string) {
		const payload: CompanyJobApprovePayload = {
			note: 'job approved'
		}

		this.jobService.postCompnayApproveJob(jobId, payload).pipe(
			tap(response => {
				if (response.Success) {
					this.toastr.success(response.Data.message);
					this.getAllPendingCompany();
				} else {
					this.toastr.error(response.Message);
				}
			}),
			catchError(() => {
				this.toastr.error('Something went wrong. Please try again');
				return of(null);
			}),
		).subscribe()
	}


	postCompnayRejectJob(jobId: string, rejectionReason: string) {
		const payload: CompanyJobRejectPayload = {
			rejection_reason: rejectionReason
		}
		this.jobService.postCompnayRejectJob(jobId, payload).pipe(
			tap(response => {
				if (response.Success) {
					this.toastr.success(response.Data.message);
					this.getAllPendingCompany();
				} else {
					this.toastr.error(response.Message);
				}
			}),
			catchError(() => {
				this.toastr.error('Something went wrong. Please try again');
				return of(null);
			}),
		).subscribe()
	}


	openJobAcceptDialogBox(jobApproval: CompanyPendingJob) {
		const dialogRef = this.dialog.open(JobAcceptPopupComponent, {
			data: {
				jobTitle: jobApproval.job_title,
				companyName: jobApproval.company_name
			}
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.postCompnayApproveJob(jobApproval.job_id)
			}
		});
	}


	openJobRejectDialogBox(jobApproval: CompanyPendingJob) {
		const dialogRef = this.dialog.open(JobRejectPopupComponent, {
			data: {
				jobTitle: jobApproval.job_title,
				companyName: jobApproval.company_name
			}
		});
		dialogRef.afterClosed().subscribe(reason => {
			if (reason) {
				this.postCompnayRejectJob(
					jobApproval.job_id,
					reason
				)
			}
		});
	}


	onSearchChange() {
		this.page = 1;
		this.searchSubject.next(this.searchTerm?.trim() || '');
	}


	selectType(type: string) {
		this.selectedType = type;
		this.typeDropdownOpen = false;
		this.getAllPendingCompany();
	}


	selectStatus(status: string) {
		this.selectedStatus = status;
		this.statusDropdownOpen = false;
		this.page = 1;
		this.getAllPendingCompany();
	}


	@HostListener('document:click', ['$event'])
	onClickOutside(event: Event) {
		const target = event.target as HTMLElement;
		// Close dropdowns if the click is not inside a select container
		if (!target.closest('.custom-select-container')) {
			this.companyDropdownOpen = false;
			this.typeDropdownOpen = false;
			this.statusDropdownOpen = false;
			this.jobRoleDropdownOpen = false;
			this.workModeDropdownOpen = false;
		}
	}


	toggleCompanyDropdown() {
		this.companyDropdownOpen = !this.companyDropdownOpen;
		this.typeDropdownOpen = false;
		this.statusDropdownOpen = false;
		this.jobRoleDropdownOpen = false;
		this.workModeDropdownOpen = false;
	}


	toggleTypeDropdown() {
		this.typeDropdownOpen = !this.typeDropdownOpen;
		this.companyDropdownOpen = false;
		this.statusDropdownOpen = false;
		this.jobRoleDropdownOpen = false;
		this.workModeDropdownOpen = false;
	}


	selectCompany(company: string) {
		this.selectedCompany = company;
		this.companyDropdownOpen = false;
		this.page = 1;
		this.getAllPendingCompany();
	}


	toggleStatusDropdown() {
		this.statusDropdownOpen = !this.statusDropdownOpen;
		this.companyDropdownOpen = false;
		this.typeDropdownOpen = false;
		this.jobRoleDropdownOpen = false;
		this.workModeDropdownOpen = false;
	}

	selectJobRole(role: string) {
		this.selectedJobRole = role;
		this.jobRoleDropdownOpen = false;
		this.page = 1;
		this.getAllPendingCompany();
	}

	toggleJobRoleDropdown() {
		this.jobRoleDropdownOpen = !this.jobRoleDropdownOpen;
		this.companyDropdownOpen = false;
		this.typeDropdownOpen = false;
		this.statusDropdownOpen = false;
		this.workModeDropdownOpen = false;
	}

	selectWorkMode(mode: string) {
		this.selectedWorkMode = mode;
		this.workModeDropdownOpen = false;
		this.page = 1;
		this.getAllPendingCompany();
	}

	toggleWorkModeDropdown() {
		this.workModeDropdownOpen = !this.workModeDropdownOpen;
		this.companyDropdownOpen = false;
		this.typeDropdownOpen = false;
		this.statusDropdownOpen = false;
		this.jobRoleDropdownOpen = false;
	}


	goToCompanyJobApprovalDetailPage(jobId: string) {
		this.router.navigate(['admin-student-platform/company-management/job-approval-detail-page', jobId], { queryParams: { origin: 'job-approval', page: this.page } })
	}


	exportToExcel(): void {

		if (!this.jobApprovals?.length) {
			this.toastr.warning('No data available to export');
			return;
		}

		const exportData = this.jobApprovals.map(
			(job: CompanyPendingJob) => ({
				'Company Name': job.company_name || '-',
				'Job Title': job.job_title || '-',
				'Job Role Category': job.job_role_category || '-',
				'Job Type': job.job_type || '-',
				'Work Mode': job.work_mode || '-',
				'Location': job.locations?.join(', ') || '-',
				'Required Skills': job.required_skills?.join(', ') || '-',
				'Openings': job.number_of_openings ?? '-',
				'Salary Min': job.salary_min ?? '-',
				'Salary Max': job.salary_max ?? '-',
				'Application Deadline': job.application_deadline
					? new Date(job.application_deadline).toLocaleDateString('en-IN')
					: '-',
				'Status': job.status || '-',
				'Submitted Date': job.submitted_at
					? new Date(job.submitted_at).toLocaleDateString('en-IN')
					: '-'
			})
		);
		const worksheet: XLSX.WorkSheet =
			XLSX.utils.json_to_sheet(exportData);

		const workbook: XLSX.WorkBook = {
			Sheets: {
				Companies: worksheet
			},
			SheetNames: ['Companies']
		};

		const excelBuffer = XLSX.write(workbook, {
			bookType: 'xlsx',
			type: 'array'
		});

		const blob = new Blob(
			[excelBuffer],
			{
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
			});
		saveAs(blob, `companies_${new Date().toISOString().slice(0, 10)}.xlsx`);
		this.toastr.success('Excel exported successfully');
	}


	sortData(column: string): void {

		if (this.sortColumn === column) {
			this.sortDirection =
				this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortColumn = column;
			this.sortDirection = 'asc';
		}

		this.jobApprovals.sort((a: any, b: any) => {

			let valueA = a[column];
			let valueB = b[column];

			if (column === 'submitted_at') {
				valueA = new Date(valueA).getTime();
				valueB = new Date(valueB).getTime();
			} else {
				valueA = valueA?.toString().toLowerCase() || '';
				valueB = valueB?.toString().toLowerCase() || '';
			}

			if (valueA < valueB) {
				return this.sortDirection === 'asc' ? -1 : 1;
			}

			if (valueA > valueB) {
				return this.sortDirection === 'asc' ? 1 : -1;
			}

			return 0;
		});
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}
